import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, TrendingDown, Wallet, UserPlus, Edit, Trash2, Users, Calendar, DollarSign, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ledgerApi, employeesApi, projectsApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export const EmployeeLedger = () => {
  const { isSuperOwner } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [showDrawForm, setShowDrawForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [commissionPreview, setCommissionPreview] = useState(null);
  const [projectProfitInfo, setProjectProfitInfo] = useState(null);
  
  const [entryFormData, setEntryFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'Payout',
    amount: '',
    description: '',
    related_project_id: ''
  });
  
  const [employeeFormData, setEmployeeFormData] = useState({
    name: '',
    pay_structure: 'W-2',
    monthly_draw: '0',
    annual_draw_limit: '0',
    draw_schedule: '1st_only',
    commission_tier_1_rate: '0.10',
    commission_tier_1_threshold: '100000',
    commission_tier_2_rate: '0.05'
  });

  const [commissionFormData, setCommissionFormData] = useState({
    project_id: '',
    net_profit_to_land_finder: '',
    description: ''
  });

  const [drawFormData, setDrawFormData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    is_split_draw: false
  });

  useEffect(() => {
    loadEmployees();
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeData(selectedEmployee.id);
    }
  }, [selectedEmployee]);

  const loadEmployees = async () => {
    try {
      const response = await employeesApi.getAll();
      setEmployees(response.data);
      if (response.data.length > 0 && !selectedEmployee) {
        setSelectedEmployee(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadEmployeeData = async (employeeId) => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        ledgerApi.getAll(employeeId),
        employeesApi.getLedgerSummary(employeeId)
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load employee data:', error);
    }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      toast.error('Please select an employee first');
      return;
    }
    try {
      const payload = {
        ...entryFormData,
        employee_id: selectedEmployee.id,
        amount: parseFloat(entryFormData.amount),
        related_project_id: entryFormData.related_project_id || null
      };
      await ledgerApi.create(payload);
      toast.success('Payout recorded');
      setShowEntryForm(false);
      setEntryFormData({
        date: new Date().toISOString().split('T')[0],
        type: 'Payout',
        amount: '',
        description: '',
        related_project_id: ''
      });
      loadEmployeeData(selectedEmployee.id);
    } catch (error) {
      console.error('Failed to create entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await ledgerApi.delete(entryId);
      toast.success('Entry deleted');
      loadEmployeeData(selectedEmployee.id);
    } catch (error) {
      console.error('Failed to delete entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const handleRecalculateBalances = async () => {
    try {
      const result = await ledgerApi.recalculateBalances();
      toast.success(result.data.message || 'Balances recalculated');
      if (selectedEmployee) {
        loadEmployeeData(selectedEmployee.id);
      }
    } catch (error) {
      console.error('Failed to recalculate:', error);
      toast.error('Failed to recalculate balances');
    }
  };

  const handleSubmitEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: employeeFormData.name,
        pay_structure: employeeFormData.pay_structure,
        monthly_draw: parseFloat(employeeFormData.monthly_draw) || 0,
        annual_draw_limit: parseFloat(employeeFormData.annual_draw_limit) || 0,
        draw_schedule: employeeFormData.draw_schedule,
        commission_tier_1_rate: parseFloat(employeeFormData.commission_tier_1_rate),
        commission_tier_1_threshold: parseFloat(employeeFormData.commission_tier_1_threshold),
        commission_tier_2_rate: parseFloat(employeeFormData.commission_tier_2_rate),
        is_active: true
      };
      
      if (editingEmployee) {
        await employeesApi.update(editingEmployee.id, payload);
        toast.success('Employee updated');
      } else {
        await employeesApi.create(payload);
        toast.success('Employee added');
      }
      
      setShowEmployeeForm(false);
      setEditingEmployee(null);
      setEmployeeFormData({
        name: '',
        pay_structure: 'W-2',
        monthly_draw: '0',
        annual_draw_limit: '0',
        draw_schedule: '1st_only',
        commission_tier_1_rate: '0.10',
        commission_tier_1_threshold: '100000',
        commission_tier_2_rate: '0.05'
      });
      loadEmployees();
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error('Failed to save employee');
    }
  };

  const handleEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeFormData({
      name: emp.name,
      pay_structure: emp.pay_structure || 'W-2',
      monthly_draw: emp.monthly_draw?.toString() || '0',
      annual_draw_limit: emp.annual_draw_limit?.toString() || '0',
      draw_schedule: emp.draw_schedule || '1st_only',
      commission_tier_1_rate: emp.commission_tier_1_rate?.toString() || '0.10',
      commission_tier_1_threshold: emp.commission_tier_1_threshold?.toString() || '100000',
      commission_tier_2_rate: emp.commission_tier_2_rate?.toString() || '0.05'
    });
    setShowEmployeeForm(true);
  };

  const handleDeleteEmployee = async (empId) => {
    try {
      await employeesApi.delete(empId);
      toast.success('Employee deactivated');
      loadEmployees();
      if (selectedEmployee?.id === empId) {
        setSelectedEmployee(null);
        setEntries([]);
        setSummary(null);
      }
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast.error('Failed to delete employee');
    }
  };

  const handleRecordCommission = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      toast.error('Please select an employee first');
      return;
    }
    try {
      const result = await employeesApi.recordCommission({
        employee_id: selectedEmployee.id,
        project_id: commissionFormData.project_id,
        net_profit_to_land_finder: parseFloat(commissionFormData.net_profit_to_land_finder),
        description: commissionFormData.description || null
      });
      
      const { commission_amount, draws_settled, pending_payout } = result.data;
      let message = `Commission of ${formatCurrency(commission_amount)} recorded.`;
      if (draws_settled > 0) {
        message += ` ${formatCurrency(draws_settled)} settled against draws.`;
      }
      if (pending_payout > 0) {
        message += ` ${formatCurrency(pending_payout)} pending payout.`;
      }
      toast.success(message);
      setShowCommissionForm(false);
      setCommissionFormData({
        project_id: '',
        net_profit_to_land_finder: '',
        description: ''
      });
      setCommissionPreview(null);
      setProjectProfitInfo(null);
      loadEmployeeData(selectedEmployee.id);
      loadEmployees();
    } catch (error) {
      console.error('Failed to record commission:', error);
      toast.error('Failed to record commission');
    }
  };

  const handlePreviewCommission = async () => {
    if (!selectedEmployee || !commissionFormData.net_profit_to_land_finder) return;
    try {
      const result = await employeesApi.previewCommission(
        selectedEmployee.id,
        parseFloat(commissionFormData.net_profit_to_land_finder)
      );
      setCommissionPreview(result.data);
    } catch (error) {
      console.error('Failed to preview commission:', error);
    }
  };

  const handleProjectSelectForCommission = async (projectId) => {
    setCommissionFormData({ ...commissionFormData, project_id: projectId });
    setProjectProfitInfo(null);
    setCommissionPreview(null);
    
    if (projectId) {
      try {
        const result = await projectsApi.getProfitInfo(projectId);
        setProjectProfitInfo(result.data);
        // Auto-populate the net profit
        if (result.data.land_finder_profit > 0) {
          setCommissionFormData(prev => ({
            ...prev,
            project_id: projectId,
            net_profit_to_land_finder: result.data.land_finder_profit.toString()
          }));
        }
      } catch (error) {
        console.error('Failed to get project profit info:', error);
      }
    }
  };

  const handleRecordDraw = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      toast.error('Please select an employee first');
      return;
    }
    try {
      const result = await employeesApi.recordMonthlyDraw({
        employee_id: selectedEmployee.id,
        amount: drawFormData.amount ? parseFloat(drawFormData.amount) : undefined,
        date: drawFormData.date,
        description: drawFormData.description || undefined,
        is_split_draw: drawFormData.is_split_draw
      });
      toast.success(`Draw of ${formatCurrency(result.data.ledger_entry.amount)} recorded`);
      setShowDrawForm(false);
      setDrawFormData({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        is_split_draw: false
      });
      loadEmployeeData(selectedEmployee.id);
    } catch (error) {
      console.error('Failed to record draw:', error);
      toast.error(error.response?.data?.detail || 'Failed to record draw');
    }
  };

  const handleGenerateAllMonthlyDraws = async () => {
    try {
      const result = await employeesApi.generateMonthlyDraws();
      toast.success(`Generated monthly draws for ${result.data.processed} employees`);
      if (selectedEmployee) {
        loadEmployeeData(selectedEmployee.id);
      }
    } catch (error) {
      console.error('Failed to generate monthly draws:', error);
      toast.error('Failed to generate monthly draws');
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'Commission_Credit':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Draw_Debit':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Monthly_Draw':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Payout':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getDrawScheduleLabel = (schedule) => {
    switch (schedule) {
      case '1st_only': return '1st of month';
      case '15th_only': return '15th of month';
      case 'both': return '1st & 15th (split)';
      default: return schedule;
    }
  };

  // Derive the selected employee's current balance from the most recent entry (entries sorted date DESC)
  const selectedEmployeeBalance = entries.length > 0 ? (entries[0].rolling_balance || 0) : 0;
  const balanceColor = selectedEmployeeBalance >= 0 ? 'text-emerald-600' : 'text-red-600';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ledger-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-ledger">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Employee Ledger</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Commission & Draw Tracking</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isSuperOwner && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRecalculateBalances}
              className="rounded-sm text-xs text-muted-foreground hover:text-foreground"
              data-testid="recalculate-balances-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Recalculate
            </Button>
          )}
          {isSuperOwner && (
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingEmployee(null);
                setEmployeeFormData({
                  name: '',
                  pay_structure: 'W-2',
                  monthly_draw: '0',
                  annual_draw_limit: '0',
                  draw_schedule: '1st_only',
                  commission_tier_1_rate: '0.10',
                  commission_tier_1_threshold: '100000',
                  commission_tier_2_rate: '0.05'
                });
                setShowEmployeeForm(true);
              }}
              className="rounded-sm"
              data-testid="add-employee-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Add Employee
            </Button>
          )}
          {selectedEmployee && (
            <>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => setShowCommissionForm(true)}
                className="rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                data-testid="record-commission-btn"
              >
                <DollarSign className="w-4 h-4 mr-1" /> Commission
              </Button>
              {isSuperOwner && (
                <Button 
                  size="sm"
                  onClick={() => setShowEntryForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm uppercase text-xs font-semibold"
                  data-testid="add-entry-btn"
                  disabled={selectedEmployeeBalance <= 0}
                  title={selectedEmployeeBalance <= 0 ? 'No outstanding balance to pay' : `Outstanding: $${selectedEmployeeBalance.toFixed(2)}`}
                >
                  <DollarSign className="w-4 h-4 mr-1" /> Payout
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Employee Tabs */}
      {employees.length > 0 ? (
        <Tabs value={selectedEmployee?.id} onValueChange={(id) => setSelectedEmployee(employees.find(e => e.id === id))}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="overflow-x-auto -mx-1 px-1">
              <TabsList className="bg-muted/50 rounded-sm">
                {employees.map((emp) => (
                  <TabsTrigger 
                    key={emp.id} 
                    value={emp.id} 
                    className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap"
                    data-testid={`employee-tab-${emp.id}`}
                  >
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    {emp.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            {selectedEmployee && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Badge variant="outline" className="rounded-sm text-xs">
                  {selectedEmployee.pay_structure || 'W-2'}
                </Badge>
                {selectedEmployee.monthly_draw > 0 && (
                  <Badge variant="outline" className="rounded-sm bg-orange-50 text-orange-700 border-orange-200 text-xs whitespace-nowrap">
                    {formatCurrency(selectedEmployee.monthly_draw)}/mo {selectedEmployee.draw_schedule === 'both' && '(split)'}
                  </Badge>
                )}
                {isSuperOwner && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEmployee(selectedEmployee)}
                      className="h-8"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Deactivate Employee</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to deactivate {selectedEmployee.name}? Their ledger history will be preserved.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteEmployee(selectedEmployee.id)}
                            className="bg-red-600 hover:bg-red-700 rounded-sm"
                          >
                            Deactivate
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            )}
          </div>

          {employees.map((emp) => (
            <TabsContent key={emp.id} value={emp.id} className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-sm border" data-testid="rolling-balance-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Current Rolling Balance</p>
                        <p className={`text-4xl font-heading font-bold mt-2 ${balanceColor}`}>
                          {formatCurrency(Math.abs(selectedEmployeeBalance))}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedEmployeeBalance > 0 
                            ? 'Pending payout'
                            : selectedEmployeeBalance === 0 
                              ? 'Balance is even' 
                              : 'Employee owes company (from draws)'
                          }
                        </p>
                        {selectedEmployeeBalance > 0 && (
                          <div className="mt-2 p-2 bg-muted/30 border rounded-sm">
                            <p className="text-xs text-blue-700 font-medium">
                              {formatCurrency(selectedEmployeeBalance)} outstanding payout
                            </p>
                          </div>
                        )}
                        {selectedEmployee?.monthly_draw > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setDrawFormData({
                                amount: '',
                                date: new Date().toISOString().split('T')[0],
                                description: '',
                                is_split_draw: selectedEmployee.draw_schedule === 'both'
                              });
                              setShowDrawForm(true);
                            }}
                            className="mt-3 rounded-sm text-xs"
                            data-testid="record-draw-btn"
                          >
                            <Calendar className="w-3 h-3 mr-1" /> Record Draw
                          </Button>
                        )}
                      </div>
                      <div className={`p-4 rounded-sm border`}>
                        <Wallet className={`w-8 h-8 ${balanceColor}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-sm border" data-testid="ytd-draws-card">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">YTD Draws</p>
                        <p className="text-3xl font-heading font-bold mt-2 text-amber-600">
                          {formatCurrency(summary?.ytd_draws || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Resets Jan 1 each year
                        </p>
                        {summary?.ytd_monthly_draws > 0 && summary?.ytd_monthly_draws !== summary?.ytd_draws && (
                          <p className="text-xs text-muted-foreground">
                            Monthly: {formatCurrency(summary.ytd_monthly_draws)}
                          </p>
                        )}
                      </div>
                      <div className="p-3 bg-white rounded-sm border">
                        <TrendingDown className="w-6 h-6 text-amber-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-sm border" data-testid="ytd-payouts-card">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">YTD Payouts</p>
                        <p className="text-3xl font-heading font-bold mt-2 text-blue-600">
                          {formatCurrency(summary?.ytd_payouts || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Payments made to employee
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-sm border">
                        <TrendingUp className="w-6 h-6 text-blue-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Ledger Table */}
              <Card className="rounded-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide">Transaction History</CardTitle>
                </CardHeader>
                <CardContent>
                  {entries.length > 0 ? (
                    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                      <table className="data-table w-full min-w-[500px]">
                        <thead>
                          <tr>
                            <th className="text-xs">Date</th>
                            <th className="text-xs">Type</th>
                            <th className="text-xs hidden sm:table-cell">Description</th>
                            <th className="text-xs">Amount</th>
                            <th className="text-xs">Balance</th>
                            <th className="text-xs w-10">Del</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map((entry) => (
                            <tr key={entry.id}>
                              <td className="font-mono text-xs sm:text-sm">{formatDate(entry.date)}</td>
                              <td>
                                <Badge variant="outline" className={`${getTypeStyles(entry.type)} rounded-sm text-[10px]`}>
                                  {entry.type === 'Payout' ? 'Payout' : entry.type.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="text-xs sm:text-sm text-muted-foreground hidden sm:table-cell">
                                {entry.description || '-'}
                                {entry.type === 'Commission_Credit' && entry.draws_settled > 0 && (
                                  <span className="block text-xs text-amber-600 mt-0.5">
                                    Settled {formatCurrency(entry.draws_settled)} in draws
                                    {entry.pending_payout > 0 && ` | ${formatCurrency(entry.pending_payout)} pending payout`}
                                  </span>
                                )}
                              </td>
                              <td className={`font-mono ${
                                entry.type === 'Commission_Credit' ? 'money-positive text-emerald-600' : 
                                entry.type === 'Payout' ? 'text-blue-600' : 'money-negative'
                              }`}>
                                {entry.type === 'Commission_Credit' ? '+' : '-'}{formatCurrency(entry.amount)}
                              </td>
                              <td className={`font-mono font-medium ${
                                entry.rolling_balance >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(entry.rolling_balance)}
                              </td>
                              <td>
                                {isSuperOwner ? (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600">
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="rounded-sm">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                        <Trash2 className="w-5 h-5" />
                                        Delete Ledger Entry
                                      </AlertDialogTitle>
                                      <AlertDialogDescription className="space-y-3">
                                        <p>Are you sure you want to delete this entry?</p>
                                        <div className="bg-white border rounded-sm p-3 text-sm">
                                          <p className="font-medium text-red-800 mb-1">⚠️ Warning</p>
                                          <ul className="text-red-700 text-xs space-y-1">
                                            <li>• {entry.type.replace('_', ' ')} - {formatCurrency(entry.amount)}</li>
                                            <li>• This will affect the rolling balance calculations</li>
                                            <li>• Consider adding a correcting entry instead</li>
                                          </ul>
                                        </div>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => handleDeleteEntry(entry.id)}
                                        className="bg-red-600 hover:bg-red-700 rounded-sm"
                                      >
                                        Yes, Delete Entry
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      No ledger entries yet. Add your first entry to start tracking.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card className="rounded-sm border">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No employees yet. Add your first employee to start tracking commissions.</p>
            <Button 
              onClick={() => setShowEmployeeForm(true)}
              className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add First Employee
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Record Payout / Add Entry Dialog */}
      <Dialog open={showEntryForm} onOpenChange={setShowEntryForm}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Payout</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEntry} className="space-y-4">
            <div className="p-3 bg-white rounded-sm border text-sm">
              <p className="text-blue-800">
                Record a payment made to <strong>{selectedEmployee?.name}</strong>.
              </p>
              {(summary?.current_balance || 0) > 0 && (
                <p className="text-blue-700 font-medium mt-1">
                  Outstanding amount: {formatCurrency(summary.current_balance)}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={entryFormData.date}
                  onChange={(e) => setEntryFormData({ ...entryFormData, date: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={entryFormData.type}
                  onValueChange={(value) => setEntryFormData({ ...entryFormData, type: value })}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Payout">Payout</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Records that a payment was made. This reduces the outstanding balance.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="text"
                value={entryFormData.amount ? Number(entryFormData.amount).toLocaleString('en-US') : ''}
                onChange={(e) => setEntryFormData({ ...entryFormData, amount: e.target.value.replace(/[^0-9.]/g, '') })}
                required
                className="rounded-sm"
                placeholder={(summary?.current_balance || 0) > 0 ? `Max: ${Number(summary.current_balance).toLocaleString('en-US')}` : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>Related Project (Optional)</Label>
              <Select
                value={entryFormData.related_project_id || 'none'}
                onValueChange={(value) => setEntryFormData({ ...entryFormData, related_project_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={entryFormData.description}
                onChange={(e) => setEntryFormData({ ...entryFormData, description: e.target.value })}
                className="rounded-sm"
                placeholder="Optional description..."
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm uppercase text-xs font-semibold"
            >
              Record Payout
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Draw Dialog */}
      <Dialog open={showDrawForm} onOpenChange={setShowDrawForm}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Draw</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordDraw} className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-sm text-sm">
              <p className="text-muted-foreground">
                Recording draw for: <strong>{selectedEmployee?.name}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Default: {formatCurrency(selectedEmployee?.monthly_draw || 0)}/mo • 
                Schedule: {getDrawScheduleLabel(selectedEmployee?.draw_schedule)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={drawFormData.date}
                  onChange={(e) => setDrawFormData({ ...drawFormData, date: e.target.value })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="text"
                  value={drawFormData.amount ? Number(drawFormData.amount).toLocaleString('en-US') : ''}
                  onChange={(e) => setDrawFormData({ ...drawFormData, amount: e.target.value.replace(/[^0-9.]/g, '') })}
                  className="rounded-sm"
                  placeholder={selectedEmployee?.draw_schedule === 'both' 
                    ? `${Number((selectedEmployee?.monthly_draw || 0) / 2).toLocaleString('en-US')} (half)` 
                    : `${Number(selectedEmployee?.monthly_draw || 0).toLocaleString('en-US')} (default)`
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use default amount
                </p>
              </div>
            </div>

            {selectedEmployee?.draw_schedule === 'both' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_split"
                  checked={drawFormData.is_split_draw}
                  onChange={(e) => setDrawFormData({ ...drawFormData, is_split_draw: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_split" className="text-sm">
                  This is a split draw (half of monthly amount)
                </Label>
              </div>
            )}

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={drawFormData.description}
                onChange={(e) => setDrawFormData({ ...drawFormData, description: e.target.value })}
                className="rounded-sm"
                placeholder="e.g., Draw 1st-15th Jan 2026"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-sm uppercase text-xs font-semibold"
            >
              Record Draw
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Commission Dialog */}
      <Dialog open={showCommissionForm} onOpenChange={(open) => {
        setShowCommissionForm(open);
        if (!open) {
          setCommissionPreview(null);
          setProjectProfitInfo(null);
          setCommissionFormData({
            project_id: '',
            net_profit_to_land_finder: '',
            description: ''
          });
        }
      }}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Sale Commission</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordCommission} className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-sm text-sm">
              <p className="text-muted-foreground">
                Recording commission for: <strong>{selectedEmployee?.name}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Current tier: {summary?.tier_1_remaining > 0 
                  ? `Tier 1 (${(summary?.tier_1_rate || 0.10) * 100}%)`
                  : `Tier 2 (${(summary?.tier_2_rate || 0.05) * 100}%)`
                }
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Project</Label>
              <Select
                value={commissionFormData.project_id}
                onValueChange={handleProjectSelectForCommission}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select sold project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.status === 'Sold' || p.status === 'Note_Held').map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.status === 'Note_Held' && <span className="text-orange-600 ml-1">(Note Held)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {projectProfitInfo && (
              <div className="p-3 bg-white rounded-sm border text-sm">
                <p className="font-medium text-blue-800">Project Profit Info</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>Sale Price: {formatCurrency(projectProfitInfo.sale_price)}</div>
                  <div>Gross Profit: {formatCurrency(projectProfitInfo.gross_profit)}</div>
                  {projectProfitInfo.has_jv && (
                    <div>Investor Profit: {formatCurrency(projectProfitInfo.investor_profit)}</div>
                  )}
                  <div className="font-semibold text-blue-700">
                    Land Finder Profit: {formatCurrency(projectProfitInfo.land_finder_profit)}
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Net Profit to Land Finder ($)</Label>
              <Input
                type="text"
                value={commissionFormData.net_profit_to_land_finder ? Number(commissionFormData.net_profit_to_land_finder).toLocaleString('en-US') : ''}
                onChange={(e) => {
                  setCommissionFormData({ ...commissionFormData, net_profit_to_land_finder: e.target.value.replace(/[^0-9.]/g, '') });
                  setCommissionPreview(null);
                }}
                required
                className="rounded-sm"
                placeholder="Auto-populated from project or enter manually"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePreviewCommission}
                disabled={!commissionFormData.net_profit_to_land_finder}
                className="rounded-sm text-xs"
              >
                Preview Commission
              </Button>
            </div>

            {commissionPreview && (
              <div className="p-4 bg-white rounded-sm border">
                <p className="font-semibold text-emerald-800">Commission Preview</p>
                <p className="text-2xl font-heading font-bold text-emerald-600 mt-2">
                  {formatCurrency(commissionPreview.commission_amount)}
                </p>
                <div className="mt-2 text-sm text-emerald-700 space-y-1">
                  {commissionPreview.tier_breakdown.map((tier, i) => (
                    <p key={i}>
                      Tier {tier.tier}: {(tier.rate * 100).toFixed(0)}% on {formatCurrency(tier.amount_applied)} = {formatCurrency(tier.commission)}
                    </p>
                  ))}
                </div>
                {/* Settlement Preview */}
                {(commissionPreview.draws_settled > 0 || commissionPreview.pending_payout > 0) && (
                  <div className="mt-3 pt-3 border-t border-emerald-200 text-sm space-y-1">
                    {commissionPreview.draws_settled > 0 && (
                      <p className="text-amber-700">
                        Settles draws: <strong>{formatCurrency(commissionPreview.draws_settled)}</strong>
                      </p>
                    )}
                    {commissionPreview.pending_payout > 0 && (
                      <p className="text-blue-700">
                        Pending payout: <strong>{formatCurrency(commissionPreview.pending_payout)}</strong>
                      </p>
                    )}
                    {commissionPreview.draws_settled > 0 && commissionPreview.pending_payout === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Commission fully absorbed by outstanding draws. No cash payout.
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Cumulative sales base after: {formatCurrency(commissionPreview.cumulative_base_after_this)}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={commissionFormData.description}
                onChange={(e) => setCommissionFormData({ ...commissionFormData, description: e.target.value })}
                className="rounded-sm"
                placeholder="Optional - defaults to project name"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={!commissionFormData.project_id || !commissionFormData.net_profit_to_land_finder}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm uppercase text-xs font-semibold"
            >
              Record Commission
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={showEmployeeForm} onOpenChange={(open) => {
        setShowEmployeeForm(open);
        if (!open) setEditingEmployee(null);
      }}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingEmployee ? 'Edit Employee' : 'Add Employee'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEmployee} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee Name *</Label>
              <Input
                value={employeeFormData.name}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, name: e.target.value })}
                required
                className="rounded-sm"
                placeholder="e.g., Land Caller"
              />
            </div>
            
            <div className="border-t pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Pay Structure</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employment Type</Label>
                  <Select
                    value={employeeFormData.pay_structure}
                    onValueChange={(value) => setEmployeeFormData({ ...employeeFormData, pay_structure: value })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="W-2">W-2 Employee</SelectItem>
                      <SelectItem value="Contractor">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Monthly Draw ($)</Label>
                  <Input
                    type="text"
                    value={employeeFormData.monthly_draw ? Number(employeeFormData.monthly_draw).toLocaleString('en-US') : ''}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, monthly_draw: e.target.value.replace(/[^0-9.]/g, '') })}
                    className="rounded-sm"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Annual Draw Limit ($)</Label>
                  <Input
                    type="text"
                    value={employeeFormData.annual_draw_limit ? Number(employeeFormData.annual_draw_limit).toLocaleString('en-US') : ''}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, annual_draw_limit: e.target.value.replace(/[^0-9.]/g, '') })}
                    className="rounded-sm"
                    placeholder="e.g., 48,000"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum draws per year (e.g., $48,000 for W-2)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Draw Schedule</Label>
                  <Select
                    value={employeeFormData.draw_schedule}
                    onValueChange={(value) => setEmployeeFormData({ ...employeeFormData, draw_schedule: value })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1st_only">1st of month only</SelectItem>
                      <SelectItem value="15th_only">15th of month only</SelectItem>
                      <SelectItem value="both">1st & 15th (split in half)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {employeeFormData.draw_schedule === 'both' 
                      ? 'Draw will be split into two payments per month'
                      : 'Full draw amount on selected date'}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Commission Structure</p>
              <p className="text-xs text-muted-foreground mb-3">
                Tiered based on <strong>cumulative LF profit (sales base)</strong>. 10% up to threshold, then lower rate forever after.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tier 1 Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={employeeFormData.commission_tier_1_rate}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, commission_tier_1_rate: e.target.value })}
                    className="rounded-sm"
                    placeholder="0.10"
                  />
                  <p className="text-xs text-muted-foreground">e.g., 0.10 = 10%</p>
                </div>
                <div className="space-y-2">
                  <Label>Tier 1 Threshold ($)</Label>
                  <Input
                    type="text"
                    value={employeeFormData.commission_tier_1_threshold ? Number(employeeFormData.commission_tier_1_threshold).toLocaleString('en-US') : ''}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, commission_tier_1_threshold: e.target.value.replace(/[^0-9.]/g, '') })}
                    className="rounded-sm"
                    placeholder="100,000"
                  />
                  <p className="text-xs text-muted-foreground">Cumulative sales cap</p>
                </div>
                <div className="space-y-2">
                  <Label>Tier 2 Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={employeeFormData.commission_tier_2_rate}
                    onChange={(e) => setEmployeeFormData({ ...employeeFormData, commission_tier_2_rate: e.target.value })}
                    className="rounded-sm"
                    placeholder="0.05"
                  />
                  <p className="text-xs text-muted-foreground">After threshold</p>
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold"
            >
              {editingEmployee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
